import { useEffect, useRef, useState } from 'react';

export default function CustomCursor() {
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isClicking, setIsClicking] = useState(false);
  const [cursorType, setCursorType] = useState('default'); // default, pointer, text
  const [enabled, setEnabled] = useState(false);

  const followerRef = useRef(null);
  const mousePos = useRef({ x: 0, y: 0 });
  const followerPos = useRef({ x: 0, y: 0 });
  const isVisibleRef = useRef(false);
  const isClickingRef = useRef(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const finePointer = window.matchMedia('(pointer: fine)').matches;
    const hoverCapable = window.matchMedia('(hover: hover)').matches;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const canEnable = finePointer && hoverCapable && !reducedMotion;

    setEnabled(canEnable);
    if (!canEnable) return undefined;

    const root = document.documentElement;
    root.classList.add('custom-cursor-enabled');

    const onMouseMove = (event) => {
      mousePos.current = { x: event.clientX, y: event.clientY };
      if (!isVisibleRef.current) {
        isVisibleRef.current = true;
        setIsVisible(true);
      }

      const target = event.target;
      if (!(target instanceof Element)) {
        setIsHovering(false);
        setCursorType('default');
        return;
      }

      const computedStyle = window.getComputedStyle(target);
      const isPointer =
        computedStyle.cursor === 'pointer' ||
        target.closest('a') ||
        target.closest('button') ||
        target.tagName === 'INPUT';

      const isText =
        !isPointer &&
        (computedStyle.cursor === 'text' ||
          ['P', 'H1', 'H2', 'H3', 'H4', 'SPAN', 'LI'].includes(target.tagName));

      setIsHovering(Boolean(isPointer));
      if (isPointer) setCursorType('pointer');
      else if (isText) setCursorType('text');
      else setCursorType('default');
    };

    const onMouseDown = () => {
      isClickingRef.current = true;
      setIsClicking(true);
    };
    const onMouseUp = () => {
      isClickingRef.current = false;
      setIsClicking(false);
    };
    const onMouseLeave = () => {
      isVisibleRef.current = false;
      setIsVisible(false);
    };
    const onMouseEnter = () => {
      isVisibleRef.current = true;
      setIsVisible(true);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mouseenter', onMouseEnter);
    document.addEventListener('mouseleave', onMouseLeave);

    let rafId = 0;
    const render = () => {
      const lerpAmount = 0.2;
      followerPos.current.x += (mousePos.current.x - followerPos.current.x) * lerpAmount;
      followerPos.current.y += (mousePos.current.y - followerPos.current.y) * lerpAmount;

      if (followerRef.current) {
        followerRef.current.style.transform = `translate3d(${followerPos.current.x}px, ${followerPos.current.y}px, 0) translate(-50%, -50%) scale(${isClickingRef.current ? 0.85 : 1})`;
      }

      rafId = window.requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mouseenter', onMouseEnter);
      document.removeEventListener('mouseleave', onMouseLeave);
      window.cancelAnimationFrame(rafId);
      root.classList.remove('custom-cursor-enabled');
    };
  }, []);

  if (!enabled) return null;

  return (
    <>
      <style jsx global>{`
        .custom-cursor-enabled,
        .custom-cursor-enabled body,
        .custom-cursor-enabled a,
        .custom-cursor-enabled button,
        .custom-cursor-enabled input {
          cursor: none !important;
        }
      `}</style>

      <div
        ref={followerRef}
        className={`pointer-events-none fixed left-0 top-0 z-[9999] flex items-center justify-center transition-all duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] ${
          isVisible ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          width: isHovering ? '64px' : cursorType === 'text' ? '2px' : '10px',
          height: isHovering ? '64px' : cursorType === 'text' ? '30px' : '10px',
          backgroundColor: isHovering ? 'rgba(249, 115, 22, 0.15)' : '#f97316',
          border: isHovering ? '1.5px solid #f97316' : '0px solid transparent',
          mixBlendMode: 'normal',
          borderRadius: cursorType === 'text' ? '2px' : '50%',
          transform: `scale(${isClicking ? 0.85 : 1})`,
          boxShadow: isHovering
            ? '0 0 30px rgba(249, 115, 22, 0.4)'
            : '0 0 10px rgba(249, 115, 22, 0.2)',
        }}
      >
        {isHovering ? <div className="h-1 w-1 animate-pulse rounded-full bg-[#f97316]" /> : null}
      </div>
    </>
  );
}
